import dayjs from 'dayjs';
import { t } from 'i18next';
import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { addressService } from 'addresses/services';
import { useAuthUser } from 'auth/hooks';
import { Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider } from 'core/states';
import { CorporateUserStatus } from 'corporate/enums';
import { corporateUserService, disciplineService } from 'corporate/services';
import { ICorporateUserAutocomplete } from 'corporate/types';
import { useJobActions } from 'jobs/hooks';
import { jobService } from 'jobs/services';
import { IJob, IUpdateJobForm } from 'jobs/types';

import { mapUpdateBaseInformationForm } from './mappers';
import { updateBaseInformationSchema } from './schema';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  job: IJob;
  onSubmitSuccess?: () => void;
}

export const UpdateBaseInformation = ({ isOpen, onClose, job, onSubmitSuccess }: IProps) => {
  const { user } = useAuthUser();
  const { upsertJob } = useJobActions();

  const displayDisciplineField = useMemo(
    () => Boolean(user?.companySettings?.bomDisciplinesEnabled),
    [user?.companySettings?.bomDisciplinesEnabled]
  );

  const defaultValues = useMemo<Partial<IUpdateJobForm>>(() => {
    if (!isOpen) {
      return {};
    }

    return {
      status:
        job.statusId && job.status
          ? {
              id: job.statusId,
              name: job.status,
            }
          : undefined,
      externalLocationRegion: job.externalLocationRegion,
      externalLocationCountry: job.externalLocationCountry
        ? {
            id: 0,
            name: job.externalLocationCountry,
          }
        : undefined,
      customerCompanyName: job.customerCompanyName,
      customerNumber: job.customerNumber,
      requestedByUser:
        job.requesterUserId && job.requesterUserName
          ? ({
              userId: job.requesterUserId,
              name: job.requesterUserName,
            } as ICorporateUserAutocomplete)
          : undefined,
      requestedByUserEmployeeId: job.requesterUserEmployeeId,
      dueDate: job.dueDate ? dayjs(job.dueDate) : undefined,
      supportCompletedDate: job.completedDate ? dayjs(job.completedDate) : undefined,
      supportUser:
        job.supportUserId && job.supportUserName
          ? ({
              userId: job.supportUserId,
              name: job.supportUserName,
            } as ICorporateUserAutocomplete)
          : undefined,
      discipline:
        displayDisciplineField && job.disciplineId && job.disciplineName
          ? {
              id: job.disciplineId,
              name: job.disciplineName,
            }
          : undefined,
    };
  }, [isOpen, job, displayDisciplineField]);

  const onSubmit = (reset: UseFormReset<IUpdateJobForm>) => async (data: IUpdateJobForm) => {
    const { success } = await upsertJob(mapUpdateBaseInformationForm(data, job));

    if (!success) {
      return;
    }

    setTimeout(() => {
      onSubmitSuccess?.();
      handleOnClose(reset);
    }, 1000);
  };

  const handleOnClose = (reset: UseFormReset<IUpdateJobForm>) => {
    reset(defaultValues);
    onClose();
  };

  return (
    <FormProvider
      schema={updateBaseInformationSchema(displayDisciplineField)}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        title={t('titleEditJobBaseInformation')}
        onSubmit={onSubmit}
      >
        <FormField
          name="status"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelStatus'),
            required: true,
            getOptions: jobService.getJobStatuses,
          }}
        />

        <FormField
          name="externalLocationRegion"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelTheatre'),
          }}
        />

        <FormField
          name="externalLocationCountry"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelExternalLocationCountry'),
            getOptions: addressService.getCountriesAutocomplete,
          }}
        />

        {displayDisciplineField && (
          <FormField
            name="discipline"
            type={InputType.AUTOCOMPLETE}
            inputProps={{
              label: t('labelDiscipline'),
              getOptions: disciplineService.getAllDisciplines,
              enabledOnFocus: false,
            }}
          />
        )}

        <FormField
          name="customerCompanyName"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelCustomerCompanyName'),
          }}
        />

        <FormField
          name="customerNumber"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelCustomerNumber'),
          }}
        />

        <FormField
          name="requestedByUser"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelRequestedByUser'),
            getOptions: corporateUserService.getCorporateUsersAutocomplete,
            requestPayload: {
              filters: {
                status: [CorporateUserStatus.ACTIVE],
                companyId: job.companyId,
              },
            },
          }}
        />

        <FormField
          name="requestedByUserEmployeeId"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelRequestedByUserEmployeeId'),
          }}
        />

        <FormField
          name="supportUser"
          type={InputType.AUTOCOMPLETE}
          inputProps={{
            label: t('labelSupportUser'),
            getOptions: corporateUserService.getCorporateUsersAutocomplete,
            requestPayload: {
              filters: {
                status: [CorporateUserStatus.ACTIVE],
                companyId: job.companyId,
              },
            },
          }}
        />

        <FormField
          name="dueDate"
          type={InputType.DATE_INPUT}
          inputProps={{
            label: t('labelDueDate'),
          }}
        />

        <FormField
          name="supportCompletedDate"
          type={InputType.DATE_INPUT}
          inputProps={{
            label: t('labelSupportCompletedDate'),
            minDate: dayjs(),
          }}
        />
      </Form>
    </FormProvider>
  );
};
